import React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Badge } from "~/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn } from "~/utils";

export interface StyledTabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  testId?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  count?: number;
  action?: React.ReactNode;
  disabled?: boolean;
  /** Tooltip text, e.g. explaining why a tab is disabled. */
  title?: string;
}

const containerVariants = cva("w-full flex items-start", {
  variants: {
    variant: {
      underline: "",
      pill: "bg-secondary-50 rounded-xl p-0",
      segmented:
        "h-full w-fit overflow-hidden rounded-lg border border-secondary-300 bg-white",
    },
    hasAction: {
      true: "items-center justify-between",
      false: "",
    },
  },
  defaultVariants: {
    variant: "underline",
    hasAction: false,
  },
});

const listVariants = cva("", {
  variants: {
    variant: {
      underline: cn(
        "h-auto rounded-none border-0 bg-transparent p-0",
        "group-data-[orientation=horizontal]/tabs:h-auto",
      ),
      pill: cn(
        "bg-transparent flex justify-start space-x-2 border-0 p-1",
        "group-data-[orientation=horizontal]/tabs:h-10",
      ),
      segmented: cn(
        "flex items-stretch divide-x divide-secondary-300 rounded-none border-0 bg-transparent p-0",
        "group-data-[orientation=horizontal]/tabs:h-full",
      ),
    },
  },
  defaultVariants: {
    variant: "underline",
  },
});

const triggerVariants = cva("", {
  variants: {
    variant: {
      underline: cn(
        "rounded-none border-0 border-b-2 border-secondary-100 px-3 text-secondary-400",
        "data-[state=active]:bg-transparent data-[state=active]:border-primary-500 data-[state=active]:text-primary-500 group-data-[variant=default]/tabs-list:data-[state=active]:shadow-none",
      ),
      pill: cn(
        "h-auto rounded-lg px-6 py-1.5 text-secondary-600 border-0",
        "data-[state=active]:bg-white data-[state=active]:shadow-xs",
        "data-[state=active]:text-black",
      ),
      segmented: cn(
        "h-full rounded-none border-0 px-5 font-normal text-secondary-700",
        "data-[state=active]:bg-primary-50 data-[state=active]:font-medium data-[state=active]:text-primary-600",
        "group-data-[variant=default]/tabs-list:data-[state=active]:shadow-none",
      ),
    },
  },
  defaultVariants: {
    variant: "underline",
  },
});

type Variant = NonNullable<VariantProps<typeof containerVariants>["variant"]>;

export interface StyledTabsNavigationProps<T extends string = string> {
  tabs: StyledTabItem<T>[];
  activeTab?: T;
  variant?: Variant;
  className?: string;
  triggerClassName?: string;
  listClassName?: string;
}

export const StyledTabsNavigation = <T extends string = string>({
  tabs,
  activeTab,
  variant,
  className,
  triggerClassName,
  listClassName,
}: StyledTabsNavigationProps<T>) => {
  const activeAction = tabs.find((tab) => tab.value === activeTab)?.action;

  return (
    <div
      className={cn(
        containerVariants({ variant, hasAction: Boolean(activeAction) }),
        className,
      )}
    >
      <TabsList className={cn(listVariants({ variant }), listClassName)}>
        {tabs.map((tab) => (
          // Wrap every trigger in a <span> hover target so the tooltip fires even
          // on a disabled <button> (Chrome drops native `title` tooltips there).
          // TooltipContent only renders when the tab has a title.
          <Tooltip key={tab.value}>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <TabsTrigger
                  value={tab.value}
                  onClick={tab.onClick}
                  disabled={tab.disabled}
                  className={cn(triggerVariants({ variant }), triggerClassName)}
                  data-testid={tab.testId}
                >
                  <div className="flex items-center gap-2">
                    {tab.icon}
                    {tab.label}
                    {tab.count !== undefined && (
                      <Badge
                        variant="secondary"
                        className="m-0 px-2 py-0 min-w-5 h-6 text-xs font-medium bg-white text-secondary-800 border border-secondary-100"
                      >
                        {tab.count}
                      </Badge>
                    )}
                  </div>
                </TabsTrigger>
              </span>
            </TooltipTrigger>
            {tab.title ? <TooltipContent>{tab.title}</TooltipContent> : null}
          </Tooltip>
        ))}
      </TabsList>
      {activeAction}
    </div>
  );
};
